import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Update a cart item quantity and refresh the cart drawer section.
   * @param {string} key - Cart item key
   * @param {number} quantity - New quantity (0 to remove)
   */
  async updateItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });

      if (!response.ok) throw new Error('Cart update failed');

      await this.#refreshSection();
    } catch (error) {
      console.error('Failed to update cart item:', error);
    }
  }

  async #refreshSection() {
    try {
      const res = await fetch('/?sections=cart-drawer');

      if (!res.ok) return;

      const data = await res.json();
      const html = data['cart-drawer'];

      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer-component');

      if (!newDrawer) return;

      // Preserve open state, replace inner content
      const isOpen = this.hasAttribute('open');
      this.innerHTML = newDrawer.innerHTML;

      if (isOpen) this.setAttribute('open', '');
    } catch {
      // Silently fail — cart will refresh on next open
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
